import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';

import { Input } from '@/components/ui/input';
import { ChipRow } from '@/shared/components/ChipRow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { AccessLogList } from '@/features/workspaces';
import { getExportOrganizationAccessLogUrl } from '@/lib/api/generated/api';
import type { AdminWorkspaceView } from '@/lib/api/generated/model';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import { RetryButton } from '@/shared/components/RetryButton';

import { useOrganizationAccessLog } from '../hooks/useAdminWorkspaces';
import {
  LOG_PRESETS,
  WORKSPACES_COPY as WORDS,
  defaultLogRange,
  presetOf,
  presetRange,
  type LogPreset,
} from '../workspacesAdmin';
import type {
  WorkspacesView,
  WorkspacesViewChange,
} from './AdminWorkspacesPage';
import { ExportButton } from './ExportButton';

/** The longest range the service reads at once (service spec 157 FR-006). */
const MAX_DAYS = 366;

function spanDays(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
}

/** As the workspaces' chips: foreground words, a primary outline when pressed (WD-04). */
const CHIP =
  'focus-visible:ring-ring touch-target inline-flex min-h-9 pointer-coarse:min-h-11 items-center gap-1.5 rounded-full border px-3 py-1 text-sm whitespace-nowrap focus-visible:ring-3 focus-visible:outline-none';
const CHIP_PRESSED = 'border-primary/50 bg-primary/10 text-foreground';
const CHIP_IDLE = 'border-border bg-card text-foreground hover:bg-accent/50';

/** The workspace select's "no filter" entry; never a workspace id, which is a UUID. */
const EVERY = 'every';

/**
 * Every workspace's access log for a range of days (spec 191, service spec 157
 * FR-006): newest first, each entry naming its workspace by the name it had then,
 * and the same range as CSV.
 *
 * Spec 195: the range is a preset or Custom, and it lives in the address with the
 * workspace the log is narrowed to, so a link opens the same view for another
 * admin. Each entry's workspace is a link that opens its sheet.
 */
export function OrganizationAccessLog({
  view,
  onChange,
  workspaces,
}: {
  view: WorkspacesView;
  onChange: WorkspacesViewChange;
  /** The organization's workspaces, once the console has read them. */
  workspaces: readonly AdminWorkspaceView[] | undefined;
}) {
  const byName = useMemo(
    () =>
      [...(workspaces ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    [workspaces],
  );
  const workspaceName = (id: string) =>
    workspaces?.find((item) => item.id === id)?.name;
  const [today] = useState(() => new Date());
  const fallback = defaultLogRange(today);
  const range = {
    from: view.from ?? fallback.from,
    to: view.to ?? fallback.to,
  };
  const preset = presetOf(range, today);
  // Custom stays open once chosen, even on days that happen to match a preset.
  const [customChosen, setCustomChosen] = useState(false);
  const custom = customChosen || preset === 'custom';
  const valid =
    range.from <= range.to && spanDays(range.from, range.to) < MAX_DAYS;
  const log = useOrganizationAccessLog(
    valid ? range.from : '',
    valid ? range.to : '',
    view.logWorkspace,
  );
  const narrowedTo = view.logWorkspace
    ? (workspaceName(view.logWorkspace) ??
      log.entries.find((entry) => entry.workspace.id === view.logWorkspace)
        ?.workspace.name)
    : undefined;

  const choosePreset = (days: LogPreset) => {
    setCustomChosen(false);
    const next = presetRange(days, today);
    // The page's own range is the address's absence, so a shared link without
    // dates keeps meaning "the last 30 days" on the day it is opened.
    onChange(days === 30 ? { from: undefined, to: undefined } : next);
  };

  return (
    <section
      aria-labelledby="admin-workspaces-activity"
      className="flex min-w-0 flex-col gap-3"
    >
      <div className="flex flex-col gap-1">
        <h2 id="admin-workspaces-activity">
          <EyebrowLabel>{WORDS.activity}</EyebrowLabel>
        </h2>
        <p className="text-muted-foreground max-w-prose text-xs">
          {WORDS.activityHint}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <ChipRow label={WORDS.rangeLabel}>
          {LOG_PRESETS.map((days) => {
            const pressed = !custom && preset === days;
            return (
              <button
                key={days}
                type="button"
                aria-pressed={pressed}
                onClick={() => choosePreset(days)}
                className={cn(CHIP, pressed ? CHIP_PRESSED : CHIP_IDLE)}
              >
                {WORDS.presets[days]}
              </button>
            );
          })}
          <button
            type="button"
            aria-pressed={custom}
            onClick={() => setCustomChosen(true)}
            className={cn(CHIP, custom ? CHIP_PRESSED : CHIP_IDLE)}
          >
            {WORDS.presets.custom}
          </button>
        </ChipRow>
        {custom ? (
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
              {WORDS.from}
              <Input
                type="date"
                value={range.from}
                max={range.to}
                onChange={(event) => {
                  // A cleared field is someone mid-edit, not a range; writing it
                  // would snap the field back to the default days.
                  if (event.target.value === '') return;
                  onChange({ from: event.target.value, to: range.to });
                }}
                className="touch-target w-auto"
              />
            </label>
            <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
              {WORDS.to}
              <Input
                type="date"
                value={range.to}
                min={range.from}
                onChange={(event) => {
                  if (event.target.value === '') return;
                  onChange({ from: range.from, to: event.target.value });
                }}
                className="touch-target w-auto"
              />
            </label>
          </div>
        ) : null}
        {/* One control narrows and widens: "All workspaces" is the way back. */}
        <Select
          value={view.logWorkspace ?? EVERY}
          onValueChange={(next) => {
            onChange({
              logWorkspace: next && next !== EVERY ? next : undefined,
            });
          }}
        >
          <SelectTrigger
            aria-label={WORDS.workspaceFilter}
            className={cn(
              CHIP,
              view.logWorkspace ? CHIP_PRESSED : CHIP_IDLE,
              'h-auto w-fit max-w-64 justify-start [&_svg]:size-3.5 [&_svg]:text-current',
            )}
          >
            <SelectValue>
              {() => (
                <span className="truncate">
                  {view.logWorkspace
                    ? WORDS.onlyWorkspace(narrowedTo ?? '…')
                    : WORDS.allWorkspaces}
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-72 overflow-y-auto">
            <SelectItem value={EVERY}>{WORDS.allWorkspaces}</SelectItem>
            {/* A workspace narrowed to by a link but gone from the list still
                has to be a value the select can hold. */}
            {view.logWorkspace &&
            !byName.some((item) => item.id === view.logWorkspace) ? (
              <SelectItem value={view.logWorkspace}>
                {narrowedTo ?? '…'}
              </SelectItem>
            ) : null}
            {byName.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ms-auto">
          <ExportButton
            label={
              view.logWorkspace ? WORDS.exportActivityAll : WORDS.exportActivity
            }
            url={getExportOrganizationAccessLogUrl(range)}
            filename={`workspace-access-log-${range.from}-to-${range.to}.csv`}
            disabled={!valid}
          />
        </div>
      </div>

      <div className="border-border bg-card rounded-md border px-4 py-2">
        {!valid ? (
          <p className="text-muted-foreground py-2 text-sm">
            {WORDS.rangeInvalid}
          </p>
        ) : log.isPending ? (
          <p className="text-muted-foreground py-2 text-sm">
            {WORDS.logLoading}
          </p>
        ) : log.isError ? (
          <div className="flex items-center gap-3 py-2">
            <p className="text-muted-foreground text-sm">{WORDS.logFailed}</p>
            <RetryButton onRetry={log.retry} />
          </div>
        ) : log.entries.length === 0 ? (
          <p className="text-muted-foreground py-2 text-sm">{WORDS.logEmpty}</p>
        ) : (
          <AccessLogList
            entries={log.entries}
            label={WORDS.activity}
            workspaceOf={(entry) =>
              // A workspace the list no longer holds (deleted since) has no
              // sheet to open; its name is only a name. Until the list is read,
              // every name is a link.
              workspaces !== undefined &&
              workspaceName(entry.workspace.id) === undefined ? (
                <span className="text-foreground font-medium">
                  {entry.workspace.name}
                </span>
              ) : (
                // A link, not a button: it has an address and opens in a new
                // tab. A plain click opens the sheet as a row does, so closing
                // it steps back here.
                <Link
                  from="/admin/workspaces"
                  to="/admin/workspaces"
                  search={(prev) => ({
                    ...prev,
                    workspace: entry.workspace.id,
                  })}
                  onClick={(event) => {
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    )
                      return;
                    event.preventDefault();
                    onChange({ workspace: entry.workspace.id }, { push: true });
                  }}
                  className="text-foreground focus-visible:ring-ring rounded-sm font-medium underline-offset-2 hover:underline focus-visible:ring-3 focus-visible:outline-none"
                >
                  {entry.workspace.name}
                </Link>
              )
            }
            hasMore={log.hasMore}
            loadingMore={log.loadingMore}
            onMore={log.loadMore}
          />
        )}
      </div>
    </section>
  );
}
